import React, { useState } from 'react';
import CalendarItemDetail from '../CalendarItemDetail/CalendarItemDetail';
import './calendarsidebar.scss';
import { useNavigate } from 'react-router-dom';
import ModalCreateEditEvent from 'views/Schedule/partials/ModalCreateEditEvent';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { softDeleteAppointment } from 'services/scheduleServices';
import usePermissionsAgenda from 'permissions/modules/agenda/usePermissionsAgenda';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { calendarComponentI18n } from '../i18n';

function formatSelectedDate(date, locale) {
  if (!date) return '';
  try {
    const formatted = date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch {
    return '';
  }
}

const CalendarSidebar = ({
  DesignSystemComponents,
  selectedDate,
  items = [],
  loading = false,
  mode = 'tasks',
  onAppointmentUpdated,
}) => {
  const { Card, IconButton } = DesignSystemComponents;

  const navigate = useNavigate();

  const { t, language: calendarLocale } = useScopedTranslation(calendarComponentI18n);

  const [editingId, setEditingId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { canViewSection } = usePermissionsAgenda();

  const handleEdit = (id) => {
    setEditingId(id);
    setShowEditModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    try {
      setDeleting(true);

      await softDeleteAppointment.deleteAppointment(deleteId);

      setShowDeleteConfirm(false);
      setDeleteId(null);

      await onAppointmentUpdated?.();
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className='calendar-sidebar d-flex flex-column'>
      <Card specialPadding='p-4'>
        <div className='main-card'>
          <div className='calendar-sidebar__header mb-3 '>
            <div className='d-flex align-items-center justify-content-between mb-1'>
              <h5 className='mb-0'>
                {mode === 'tasks' ? t('sidebar_tasks_title') : t('sidebar_events_title')}
              </h5>
              {mode === 'appointments' && canViewSection && (
                <IconButton
                  icon='list'
                  onClick={() => navigate('/agenda')}
                  size='lg'
                  iconStroke='var(--primary-600)'
                />
              )}
            </div>
            <div className='mb-2'>
              <span className='color-gray-600'>
                {formatSelectedDate(selectedDate, calendarLocale)}
              </span>
            </div>
          </div>
          <div className='calendar-sidebar__content task-list'>
            {loading && (
              <div className='d-flex align-items-center justify-content-center color-gray-600 small h-100'>
                {mode === 'tasks' ? t('sidebar_loading_tasks') : t('sidebar_loading_events')}
              </div>
            )}
            {!loading && items && items.length > 0 && (
              <div className='calendar-sidebar__tasks d-flex flex-column gap-2'>
                {items.map((item) => {
                  if (mode === 'tasks') {
                    return (
                      <CalendarItemDetail
                        DesignSystemComponents={DesignSystemComponents}
                        key={item.id}
                        title={item.title}
                        time={item.time}
                        code={item.code}
                        clientName={item.clientName}
                        assigneeName={item.assigneeName}
                        status={item.status}
                        priority={item.priority}
                        caseId={item.caseId}
                        taskId={item.taskId}
                      />
                    );
                  }

                  return (
                    <CalendarItemDetail
                      DesignSystemComponents={DesignSystemComponents}
                      key={item.id}
                      title={item.title}
                      time={item.time}
                      mode='appointments'
                      origin={item.origin}
                      description={item.description}
                      startDateTime={item.startDateTime}
                      endDateTime={item.endDateTime}
                      appointmentId={item.id}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                    />
                  );
                })}
              </div>
            )}

            {!loading && (!items || items.length === 0) && (
              <div className='d-flex align-items-center justify-content-center color-gray-600 small h-100 text-center'>
                {mode === 'tasks' ? t('sidebar_empty_tasks') : t('sidebar_empty_events')}
              </div>
            )}
          </div>
        </div>
      </Card>
      {showEditModal && (
        <ModalCreateEditEvent
          DesignSystemComponents={DesignSystemComponents}
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingId(null);
          }}
          onCreated={async () => {
            setShowEditModal(false);
            setEditingId(null);

            await onAppointmentUpdated?.();
          }}
          appointmentId={editingId}
        />
      )}
      <ConfirmModal
        Modal={DesignSystemComponents.Modal}
        show={showDeleteConfirm}
        title={t('sidebar_delete_title')}
        message={t('sidebar_delete_message')}
        confirmLabel={deleting ? t('sidebar_deleting') : t('sidebar_delete_confirm')}
        confirmVariant='danger'
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (deleting) return;
          setShowDeleteConfirm(false);
          setDeleteId(null);
        }}
      />
    </div>
  );
};

export default CalendarSidebar;
